import Controller from '@ember/controller';
import { action, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe, type TrustedHTML } from '@ember/template';
import { cached, tracked } from '@glimmer/tracking';
import type { Market } from 'frontend-utils/models';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';
import { ISSUE_TYPES, type IssueType } from 'lumen/models/parity-manager/issue';
import type MarketParityParityManagerMailRoute from 'lumen/routes/market/parity/parity-manager/mail';
import type { MailTemplate } from 'lumen/routes/market/parity/parity-manager/mail';
import type AjaxService from 'lumen/services/ajax';
import { MAIL_TYPE } from 'lumen/utils/constants/pm-constant-values';
import type { ModelFrom } from 'lumen/utils/routes';

export const RECEIVERS_LIMIT = 10;
export const CC_RECEIVERS_LIMIT = 5;
export const BCC_RECEIVERS_LIMIT = 5;

export const MAX_LENGTH_FREE_TEXT = 400;

interface TemplateOption {
  content: TrustedHTML;
  id: number;
  isFreeTextAllowed: boolean;
  label: string;
}

function parseReceivers(string: string, separator = ';'): string[] {
  if (!string.length) {
    return [];
  }

  const trimmedValues = string
    .split(separator)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return [...new Set(trimmedValues)];
}

const QUERY_PARAMS = [
  {
    issueId: { scope: 'controller' },
    subscriptionId: { scope: 'controller' },
    issueType: { scope: 'controller' },
    mailType: { scope: 'controller' },
  },
] as const;

export default class MarketParityParityManagerMailController extends Controller {
  declare model: ModelFrom<MarketParityParityManagerMailRoute>;

  // @ts-expect-error: Property 'type' is missing in type '{ readonly scope: "controller"; }' but required in type '{ type: ControllerQueryParamType; }'.
  queryParams: Controller['queryParams'] = QUERY_PARAMS;

  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];

  @tracked bccReceivers = '';
  @tracked ccReceivers = '';
  @tracked freeText = '';
  @tracked isSendingMail = false;
  @tracked issueId = '';
  @tracked issueType: IssueType | null = null;
  @tracked mailSubject = '';
  @tracked mailType = '';
  @tracked receivers = '';
  @tracked selectedTemplateId: number | null = null;
  @tracked subscriptionId = '';

  get bccReceiversArray(): string[] {
    return parseReceivers(this.bccReceivers);
  }

  get bccReceiversErrorMessage(): string | undefined {
    if (this.hasInvalidBCCReceiver) {
      return this.intl.t('settings.reports.error.invalid_email');
    }
    if (this.hasTooManyBCCReceivers) {
      return this.intl.t('parity_manager.too_many_receivers_error.count', {
        count: BCC_RECEIVERS_LIMIT,
      });
    }
    return undefined;
  }

  get ccReceiversArray(): string[] {
    return parseReceivers(this.ccReceivers);
  }

  get ccReceiversErrorMessage(): string | undefined {
    if (this.hasInvalidCCReceiver) {
      return this.intl.t('settings.reports.error.invalid_email');
    }
    if (this.hasTooManyCCReceivers) {
      return this.intl.t('parity_manager.too_many_receivers_error.count', {
        count: CC_RECEIVERS_LIMIT,
      });
    }
    return undefined;
  }

  get hasBCCReceiversError(): boolean {
    return this.bccReceiversErrorMessage !== undefined;
  }

  get hasCCReceiversError(): boolean {
    return this.ccReceiversErrorMessage !== undefined;
  }

  get hasFreeTextError(): boolean {
    return this.freeText.length > MAX_LENGTH_FREE_TEXT;
  }

  get hasInvalidBCCReceiver(): boolean {
    return this.bccReceiversArray.some((value) => !isValidEmailAddress(value));
  }

  get hasInvalidCCReceiver(): boolean {
    return this.ccReceiversArray.some((value) => !isValidEmailAddress(value));
  }

  get hasInvalidReceiver(): boolean {
    return this.receiversArray.some((value) => !isValidEmailAddress(value));
  }

  get hasReceiversError(): boolean {
    return this.receiversErrorMessage !== undefined;
  }

  get hasTooManyBCCReceivers(): boolean {
    return this.bccReceiversArray.length > BCC_RECEIVERS_LIMIT;
  }

  get hasTooManyCCReceivers(): boolean {
    return this.ccReceiversArray.length > CC_RECEIVERS_LIMIT;
  }

  get hasTooManyReceivers(): boolean {
    return this.receiversArray.length > RECEIVERS_LIMIT;
  }

  get isCopyTextFlow(): boolean {
    // Currently Copy text flow is used in case of Test Bookings + wholesaler mails. Hotel mails are always HTML templates.
    return (
      this.issueType === ISSUE_TYPES.TEST_BOOKING &&
      this.mailType === MAIL_TYPE.WHOLESALER
    );
  }

  get isMajorOTAFlow(): boolean {
    return this.issueType === ISSUE_TYPES.MAJOR_OTA;
  }

  get isModalContentSize(): boolean {
    // should be true if we show the smaller content-sized modal (because the copyable textarea is much smaller)
    return this.isCopyTextFlow;
  }

  get isSendButtonDisabled(): boolean {
    const hasRequiredFields =
      this.mailSubject.length > 0 && this.receiversArray.length > 0;

    const hasAnyInvalidReceiver =
      this.hasInvalidReceiver ||
      this.hasInvalidCCReceiver ||
      this.hasInvalidBCCReceiver;

    const hasTooManyReceivers =
      this.hasTooManyReceivers ||
      this.hasTooManyCCReceivers ||
      this.hasTooManyBCCReceivers;

    return (
      this.isSendingMail ||
      hasAnyInvalidReceiver ||
      hasTooManyReceivers ||
      !hasRequiredFields
    );
  }

  get market(): Market | null {
    return this.hotelAndMarketService.market;
  }

  get receiversArray(): string[] {
    return parseReceivers(this.receivers);
  }

  get receiversErrorMessage(): string | undefined {
    if (this.hasInvalidReceiver) {
      return this.intl.t('settings.reports.error.invalid_email');
    }
    if (this.hasTooManyReceivers) {
      return this.intl.t('parity_manager.too_many_receivers_error.count', {
        count: RECEIVERS_LIMIT,
      });
    }
    return undefined;
  }

  get selectedTemplate(): TemplateOption | undefined {
    if (!this.selectedTemplateId) {
      return this.templateOptions[0];
    }
    return this.templateOptions.find(
      (template) => template.id === this.selectedTemplateId,
    );
  }

  get sortedTemplates(): MailTemplate[] {
    if (this.model.isLoading) {
      return [];
    }
    return this.model.templates.sort((a, b) => a.name.localeCompare(b.name));
  }

  @cached
  get templateOptions(): TemplateOption[] {
    return this.sortedTemplates.map((template) => ({
      id: template.id,
      label: template.name,
      content: htmlSafe(template.content),
      isFreeTextAllowed: template.with_free_text,
    }));
  }

  get templateStringContent(): string | undefined {
    return this.selectedTemplate?.content.toString();
  }

  showErrorPopup(customMessage = ''): void {
    if (customMessage) {
      void this.alerting.showErrorAlert({
        text: customMessage,
      });
    } else {
      void this.alerting.showErrorAlert();
    }
  }

  @action
  async copyText(): Promise<void> {
    // Used in case of the wholesaler flow: retrieve the textarea so that we can copy its text
    const violationMailTextArea = document.getElementById(
      'email-textarea',
    ) as HTMLTextAreaElement;

    if (violationMailTextArea) {
      // We select the textarea and the execCommand should copy the selection to the clipboard.
      // This allows users to easily copy & paste everything in their mail client.
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await violationMailTextArea.select();
      // https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand
      document.execCommand('copy');
    }
  }

  @action onTemplateChange(option: { id: number; label: string }): void {
    this.selectedTemplateId = option.id;
  }

  @action redirectOnClose(): void {
    this.selectedTemplateId = null;
    this.router.transitionTo('market.parity.parity-manager');
    this.router.refresh('market.parity.parity-manager');
  }

  @action sendMail(): void {
    // Note: sendMail is used to send HTML emails. This is allowed if you're not in the Copy Text scenario
    if (
      !this.isCopyTextFlow &&
      this.issueId &&
      this.subscriptionId &&
      this.receivers &&
      !this.isSendingMail &&
      this.mailType &&
      this.receiversArray.length
    ) {
      const requestURL = `${this.ajax.host}/apigateway/v1/app/paritymanager/send_email?market_id=${this.market?.id}&subscription_id=${this.subscriptionId}`;
      const mailData = {
        recipient_type: 1,
        notification_type: 1,
        receivers: this.receiversArray,
        cc_receivers: this.ccReceiversArray,
        bcc_receivers: this.bccReceiversArray,
        issue_id: Number(this.issueId),
        subject: this.mailSubject,
        free_text: this.freeText,
        mail_type: Number(this.mailType),
        template_id: this.selectedTemplate?.id,
      };

      set(this, 'isSendingMail', true);

      this.ajax
        .request(requestURL, {
          method: 'POST',
          dataType: 'json',
          contentType: 'application/json; charset=utf-8',
          data: JSON.stringify(mailData),
        })
        .then(() => {
          set(this, 'isSendingMail', false);

          this.send('redirectOnClose');

          this.toast.success(
            this.intl.t('parity_manager.mail_sent_successfully'),
            {
              showIcon: true,
            },
          );
        })
        .catch((error) => {
          this.errorTracking.captureException(error);

          set(this, 'isSendingMail', false);

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (error.payload && error.payload.user_msg) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            this.showErrorPopup(error.payload.user_msg);
          } else {
            this.showErrorPopup();
          }
        });
    }
  }

  @action updateBccReceivers(event: Event): void {
    this.bccReceivers = (event.target as HTMLInputElement).value;
  }

  @action updateCcReceivers(event: Event): void {
    this.ccReceivers = (event.target as HTMLInputElement).value;
  }

  @action updateFreeText(event: Event): void {
    this.freeText = (event.target as HTMLInputElement).value;
  }

  @action updateMailSubject(event: Event): void {
    this.mailSubject = (event.target as HTMLInputElement).value;
  }

  @action updateReceivers(event: Event): void {
    this.receivers = (event.target as HTMLInputElement).value;
  }
}
